import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import SearchFilters from './components/SearchFilters';
import SearchHeader from './components/SearchHeader';
import PropertyCard from './components/PropertyCard';
import PropertyListView from './components/PropertyListView';
import PropertyMap from './components/PropertyMap';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Skeleton from '../../components/ui/Skeleton';
import { propertyService } from '../../services/propertyService';
import { communeService } from '../../services/communeService';
import { createPropertyInquiryFromClick } from '../../services/propertyInquiryService';
import EmptyState from '../../components/ui/EmptyState';
import { trackPropertySearch } from '../../services/userJourneyService';
import { gaTrackPropertySearch } from '../../services/gaAnalyticsService';

const PropertySearchListings = () => {
  const location = useLocation();
  const [isFiltersVisible, setIsFiltersVisible] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('relevance');
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showInquirySuccess, setShowInquirySuccess] = useState(false);
  const [error, setError] = useState('');

  // Real data from Supabase
  const [properties, setProperties] = useState([]);
  const [communes, setCommunes] = useState([]);

  const [filters, setFilters] = useState({
    commune: '',
    propertyType: '',
    minPrice: '',
    maxPrice: '',
    bedrooms: '',
    bathrooms: '',
    proximity: '',
    securityLevel: '',
    hasGenerator: false,
    hasParking: false,
    furnished: false
  });

  // Load initial data from Supabase
  useEffect(() => {
    loadInitialData();
  }, []);

  // Load persisted filters from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem('kapital_search_filters');
      if (saved) {
        const persisted = JSON.parse(saved);
        setFilters(persisted);
      }
    } catch (e) {}
  }, []);

  const loadInitialData = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      // Load properties and communes concurrently
      const [propertiesResponse, communesResponse] = await Promise.all([
        propertyService?.getAllProperties(),
        communeService?.getAllCommunes()
      ]);

      // Handle the response structure properly
      if (propertiesResponse?.error) {
        setError(propertiesResponse?.error);
        setProperties([]);
      } else {
        setProperties(propertiesResponse?.data || []);
      }

      if (communesResponse?.error) {
        console.warn('Error loading communes:', communesResponse?.error);
        setCommunes([]);
      } else {
        setCommunes(communesResponse?.data || []);
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
      setError('Erreur lors du chargement des données. Veuillez actualiser la page.');
      setProperties([]);
      setCommunes([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Map property data to frontend format
  const mapPropertyToFrontend = (property) => {
    // Get commune name from communes array
    const commune = communes?.find(c => c?.id === property?.commune_id);
    
    return {
      id: property?.id,
      title: property?.title,
      commune: commune?.name || 'Unknown',
      type: property?.property_type?.charAt(0)?.toUpperCase() + property?.property_type?.slice(1) || 'Unknown',
      price: parseFloat(property?.price) || 0,
      pricePerSqm: property?.surface_area ? (parseFloat(property?.price) / parseFloat(property?.surface_area)) : 0,
      area: parseFloat(property?.surface_area) || 0,
      bedrooms: property?.bedrooms,
      bathrooms: property?.bathrooms,
      description: property?.description || '',
      thumbnail: property?.images?.[0] || "https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=800&h=600&fit=crop",
      videoUrl: property?.videos?.[0]?.url || null,
      verificationStatus: property?.status === 'available' ? 'verified' : 'pending',
      estimatedROI: Math.random() * 10 + 5, // Mock ROI calculation
      hasGenerator: property?.features?.generator || false,
      hasParking: property?.features?.parking || false,
      securityLevel: property?.features?.security_level || 'standard',
      furnished: property?.features?.furnished || false,
      priceHistory: true,
      coordinates: property?.coordinates
    };
  };

  // Filter properties based on current filters and search
  const filteredProperties = Array.isArray(properties) ? properties?.map(mapPropertyToFrontend)?.filter(property => {
    // Search query filter
    if (searchQuery) {
      const searchLower = searchQuery?.toLowerCase();
      const matchesSearch = 
        property?.title?.toLowerCase()?.includes(searchLower) ||
        property?.commune?.toLowerCase()?.includes(searchLower) ||
        property?.type?.toLowerCase()?.includes(searchLower) ||
        property?.description?.toLowerCase()?.includes(searchLower);
      
      if (!matchesSearch) return false;
    }

    // Commune filter
    if (filters?.commune && property?.commune?.toLowerCase() !== filters?.commune?.toLowerCase()) {
      return false;
    }

    // Property type filter
    if (filters?.propertyType && property?.type?.toLowerCase() !== filters?.propertyType?.toLowerCase()) {
      return false;
    }

    // Price range filter
    if (filters?.minPrice && property?.price < parseInt(filters?.minPrice)) {
      return false;
    }
    if (filters?.maxPrice && property?.price > parseInt(filters?.maxPrice)) {
      return false;
    }

    // Bedrooms filter
    if (filters?.bedrooms && property?.bedrooms !== parseInt(filters?.bedrooms)) {
      return false;
    }

    // Bathrooms filter
    if (filters?.bathrooms && property?.bathrooms !== parseInt(filters?.bathrooms)) {
      return false;
    }

    // Security level filter
    if (filters?.securityLevel && property?.securityLevel !== filters?.securityLevel) {
      return false;
    }

    // Amenities filters
    if (filters?.hasGenerator && !property?.hasGenerator) {
      return false;
    }
    if (filters?.hasParking && !property?.hasParking) {
      return false;
    }
    if (filters?.furnished && !property?.furnished) {
      return false;
    }

    return true;
  }) : [];

  // Sort properties
  const sortedProperties = Array.isArray(filteredProperties) ? [...filteredProperties]?.sort((a, b) => {
    switch (sortBy) {
      case 'price-asc':
        return a?.price - b?.price;
      case 'price-desc':
        return b?.price - a?.price;
      case 'area-desc':
        return b?.area - a?.area;
      case 'roi-desc':
        return (b?.estimatedROI || 0) - (a?.estimatedROI || 0);
      case 'date-desc':
        return b?.id?.localeCompare(a?.id) || 0; // Sort by ID as proxy for date
      default:
        return 0; // relevance - keep original order
    }
  }) : [];

  const handleFiltersChange = (newFilters) => {
    setIsLoading(true);
    setFilters(newFilters);

    // Track filter-based search
    trackPropertySearch({
      commune: newFilters?.commune || '',
      propertyType: newFilters?.propertyType || '',
      minPrice: newFilters?.minPrice ? parseInt(newFilters?.minPrice) : null,
      maxPrice: newFilters?.maxPrice ? parseInt(newFilters?.maxPrice) : null,
      resultsCount: filteredProperties?.length,
    });
    gaTrackPropertySearch({
      commune: newFilters?.commune || '',
      propertyType: newFilters?.propertyType || '',
      minPrice: newFilters?.minPrice ? parseInt(newFilters?.minPrice) : null,
      maxPrice: newFilters?.maxPrice ? parseInt(newFilters?.maxPrice) : null,
      resultsCount: filteredProperties?.length,
    });

    // Simulate filter processing delay
    setTimeout(() => {
      setIsLoading(false);
    }, 300);
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      commune: '',
      propertyType: '',
      minPrice: '',
      maxPrice: '',
      bedrooms: '',
      bathrooms: '',
      proximity: '',
      securityLevel: '',
      hasGenerator: false,
      hasParking: false,
      furnished: false
    };
    setFilters(clearedFilters);
    setSearchQuery('');
  };

  const handleInquiry = async (inquiryData) => {
    console.log('Property inquiry:', inquiryData);
    
    // Save inquiry to Supabase database
    try {
      const { data, error } = await createPropertyInquiryFromClick(inquiryData);
      
      if (error) {
        console.error('Error saving inquiry:', error);
        // Show error to user but still show success briefly for UX
        setShowInquirySuccess(true);
        setTimeout(() => {
          setShowInquirySuccess(false);
        }, 2000);
        return;
      }

      console.log('Inquiry saved successfully:', data);
      setShowInquirySuccess(true);
      setTimeout(() => {
        setShowInquirySuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Error in handleInquiry:', error);
      // Still show success for UX, but log the error
      setShowInquirySuccess(true);
      setTimeout(() => {
        setShowInquirySuccess(false);
      }, 2000);
    }
  };

  const handleSearchChange = (query) => {
    setIsLoading(true);
    setSearchQuery(query);

    if (query?.trim()?.length > 2) {
      trackPropertySearch({ query: query?.trim(), resultsCount: filteredProperties?.length });
      gaTrackPropertySearch({ query: query?.trim(), resultsCount: filteredProperties?.length });
    }

    // Simulate search delay
    setTimeout(() => {
      setIsLoading(false);
    }, 200);
  };

  // Parse URL parameters on mount
  useEffect(() => {
    const params = new URLSearchParams(location?.search);
    const commune = params?.get('commune');
    const type = params?.get('type');
    
    if (commune || type) {
      setFilters(prev => ({
        ...prev,
        commune: commune || '',
        propertyType: type || ''
      }));
    }
  }, [location?.search]);

  // Show error state
  if (error && !isLoading) {
    return (
      <div className="bg-white min-h-screen">
        <Header />
        <main className="pt-16 relative z-10">
          <div className="container mx-auto px-4 py-8">
            <div className="text-center py-12">
              <Icon name="AlertCircle" size={48} color="var(--color-error)" className="mx-auto mb-4" />
              <h3 className="font-heading font-semibold text-xl text-text-primary mb-2">
                Erreur de chargement
              </h3>
              <p className="text-text-secondary mb-6">{error}</p>
              <Button
                variant="outline"
                onClick={loadInitialData}
                iconName="RefreshCw"
                iconPosition="left"
              >
                Réessayer
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen">
      {/* Background Layer removed for white background */}
      <Header />
      <main className="relative z-10">
        <div className="bg-white">
          <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-8">
            {/* Page Header */}
            <div className="mb-4 sm:mb-6">
              <div className="flex items-center space-x-2 text-sm text-text-secondary mb-2">
                <span>Accueil</span>
                <Icon name="ChevronRight" size={14} />
                <span className="text-brand-gold">Recherche de propriétés</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0 pr-3">
                  <h1 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl text-text-primary leading-tight">
                    Recherche de propriétés
                  </h1>
                  <p className="text-text-secondary mt-1 text-sm sm:text-base">
                    Découvrez les meilleures opportunités immobilières à Abidjan
                    {!isLoading && (
                      <span className="ml-2 text-brand-gold font-medium">
                        ({sortedProperties?.length} propriété{sortedProperties?.length !== 1 ? 's' : ''} disponible{sortedProperties?.length !== 1 ? 's' : ''})
                      </span>
                    )}
                  </p>
                </div>
                {/* Mobile filter toggle button */}
                <button
                  type="button"
                  onClick={() => setIsFiltersVisible(!isFiltersVisible)}
                  className="lg:hidden flex-shrink-0 flex items-center space-x-2 px-3 sm:px-4 py-2.5 bg-white border border-gray-200 rounded-xl shadow-sm active:bg-gray-50 active:scale-95 transition-all duration-150 relative touch-manipulation min-h-[44px]"
                >
                  <Icon name="SlidersHorizontal" size={18} color="var(--color-brand-gold)" />
                  <span className="text-sm font-medium text-text-primary hidden xs:inline">Filtres</span>
                  {(() => {
                    const count = [
                      filters?.commune, filters?.propertyType, filters?.minPrice,
                      filters?.maxPrice, filters?.bedrooms, filters?.bathrooms,
                      filters?.proximity, filters?.securityLevel,
                      filters?.hasGenerator, filters?.hasParking, filters?.furnished
                    ]?.filter(Boolean)?.length;
                    return count > 0 ? (
                      <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-brand-gold text-white text-xs font-bold rounded-full flex items-center justify-center">
                        {count}
                      </span>
                    ) : null;
                  })()}
                </button>
              </div>
            </div>

            <div className="flex flex-col lg:flex-row gap-4 sm:gap-8">
              {/* Filters Sidebar — desktop: sticky sidebar, mobile: bottom sheet handled inside SearchFilters */}
              <div className="lg:w-80 lg:flex-shrink-0">
                <SearchFilters
                  filters={filters}
                  onFiltersChange={handleFiltersChange}
                  onClearFilters={handleClearFilters}
                  isVisible={isFiltersVisible}
                  onToggle={() => setIsFiltersVisible(!isFiltersVisible)}
                  communes={communes}
                />
              </div>

              {/* Main Content */}
              <div className="flex-1 space-y-4 sm:space-y-6">
                {/* Search Header */}
                <SearchHeader
                  searchQuery={searchQuery}
                  onSearchChange={handleSearchChange}
                  sortBy={sortBy}
                  onSortChange={setSortBy}
                  viewMode={viewMode}
                  onViewModeChange={setViewMode}
                  resultsCount={sortedProperties?.length}
                  isLoading={isLoading}
                />

                {/* Results */}
                {isLoading ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
                    {[...Array(6)]?.map((_, i) => (
                      <div key={i} className="bg-card border border-border rounded-xl overflow-hidden">
                        {/* Image skeleton */}
                        <Skeleton className="w-full h-48" />
                        <div className="p-4 space-y-3">
                          {/* Title */}
                          <Skeleton className="h-5 w-3/4" />
                          {/* Location */}
                          <Skeleton className="h-4 w-1/2" />
                          {/* Price */}
                          <Skeleton className="h-6 w-2/5" />
                          {/* Meta row */}
                          <div className="flex gap-3 pt-1">
                            <Skeleton className="h-4 w-16" />
                            <Skeleton className="h-4 w-16" />
                            <Skeleton className="h-4 w-16" />
                          </div>
                          {/* Button */}
                          <Skeleton className="h-9 w-full mt-2" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : sortedProperties?.length === 0 ? (
                  <EmptyState
                    iconName="Search"
                    title="Aucune propriété trouvée"
                    description="Essayez de modifier vos critères de recherche ou de supprimer certains filtres."
                    ctaLabel="Réinitialiser les filtres"
                    ctaIcon="RotateCcw"
                    ctaVariant="outline"
                    onCta={handleClearFilters}
                    className="py-16"
                  />
                ) : (
                  <>
                    {viewMode === 'grid' && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
                        {sortedProperties?.map((property) => (
                          <PropertyCard
                            key={property?.id}
                            property={property}
                            onInquiry={handleInquiry}
                          />
                        ))}
                      </div>
                    )}

                    {viewMode === 'list' && (
                      <PropertyListView
                        properties={sortedProperties}
                        onInquiry={handleInquiry}
                      />
                    )}

                    {viewMode === 'map' && (
                      <div className="h-72 sm:h-96 lg:h-[600px] rounded-xl overflow-hidden">
                        <PropertyMap
                          properties={sortedProperties}
                          selectedProperty={selectedProperty}
                          onPropertySelect={setSelectedProperty}
                          filters={filters}
                        />
                      </div>
                    )}

                    {/* Load More Button - Future pagination */}
                    {sortedProperties?.length >= 10 && (
                      <div className="text-center pt-6 sm:pt-8">
                        <Button
                          variant="outline"
                          size="lg"
                          iconName="ChevronDown"
                          iconPosition="right"
                          onClick={() => {
                            console.log('Load more properties...');
                          }}
                        >
                          Charger plus de propriétés
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      {/* Success Toast */}
      {showInquirySuccess && (
        <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-auto bg-success text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center space-x-2">
          <Icon name="CheckCircle" size={20} color="white" />
          <span>Demande envoyée avec succès!</span>
        </div>
      )}
    </div>
  );
};

export default PropertySearchListings;